import { useState } from "react";
import { Volume2, MessageSquare, User, Users, ChevronRight, PenLine } from "lucide-react";
import conversations from "../data/conversations.json";

function speak(text) {
  if ("speechSynthesis" in window) {
    const u = new SpeechSynthesisUtterance(text);
    u.rate = 0.8;
    u.lang = "en-US";
    window.speechSynthesis.speak(u);
  }
}

export default function Conversations() {
  const [activeId, setActiveId] = useState(null);
  const activeConvo = conversations.find((c) => c.id === activeId);

  if (activeConvo) {
    return (
      <div className="space-y-6">
        <button onClick={() => setActiveId(null)} className="text-gold-700 hover:underline text-sm">&larr; Back to all conversations</button>

        <div className="bg-white rounded-2xl shadow-lg p-8">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${activeConvo.level === "beginner" ? "bg-sage-light text-sage-dark" : "bg-gold-100 text-gold-700"}`}>
              {activeConvo.level}
            </span>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">{activeConvo.title}</h1>
          <p className="text-gray-500 mb-6">{activeConvo.situation}</p>

          {/* Dialogue */}
          <div className="space-y-4">
            {activeConvo.dialogue.map((line, i) => {
              const isYou = line.speaker === "You";
              return (
                <div key={i} className={`flex ${isYou ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[85%] ${isYou ? "items-end" : "items-start"}`}>
                    <div className="flex items-center gap-2 mb-1">
                      {!isYou && (
                        <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center">
                          <Users className="w-3.5 h-3.5 text-gray-500" />
                        </div>
                      )}
                      <span className="text-xs font-semibold text-gray-400">{line.speaker}</span>
                      {isYou && (
                        <div className="w-6 h-6 bg-cream-200 rounded-full flex items-center justify-center">
                          <User className="w-3.5 h-3.5 text-gold-700" />
                        </div>
                      )}
                    </div>
                    <div className={`rounded-2xl p-4 ${isYou ? "bg-ink-900 text-white rounded-tr-sm" : "bg-gray-100 text-gray-800 rounded-tl-sm"}`}>
                      <div className="flex items-start gap-2">
                        <p className="flex-1">{line.text}</p>
                        <button onClick={() => speak(line.text)} className={`p-1.5 rounded-full flex-shrink-0 ${isYou ? "bg-ink-900 hover:bg-ink-100" : "bg-gray-200 hover:bg-gray-300"} transition-all`}>
                          <Volume2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                    {line.tip && (
                      <p className={`text-xs mt-1 px-2 ${isYou ? "text-right text-ink-500" : "text-gray-400"}`}>
                        Tip: {line.tip}
                      </p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Key Phrases */}
        <div className="bg-white rounded-2xl shadow-lg p-8">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Key Phrases to Remember</h2>
          <div className="flex flex-wrap gap-2">
            {activeConvo.keyPhrases.map((phrase, i) => (
              <button key={i} onClick={() => speak(phrase)} className="inline-flex items-center gap-2 bg-paper-deep text-gold-700 px-4 py-2 rounded-xl font-medium hover:bg-cream-100 border border-ink-100 transition-all">
                <Volume2 className="w-3.5 h-3.5" />
                {phrase}
              </button>
            ))}
          </div>
        </div>

        {/* Practice Prompt */}
        <div className="bg-gold-50 border-2 border-gold-300 rounded-2xl p-6">
          <div className="flex items-start gap-3">
            <PenLine className="w-6 h-6 text-gold-700 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-gold-700 mb-1">Your Turn! Practice:</h3>
              <p className="text-gold-700">{activeConvo.practice}</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 page-enter relative">
      <div className="blob blob-pink" style={{ width: 320, height: 320, top: -40, right: -40, opacity: 0.18 }} />
      <div className="blob blob-purple" style={{ width: 260, height: 260, top: 280, left: -40, opacity: 0.15 }} />
      <div className="relative">
        <h1 className="font-display text-4xl md:text-5xl font-normal mb-2"><em>Conversations.</em></h1>
        <p className="text-gray-600 dark:text-gray-300 font-semibold">Learn real English dialogues for everyday situations.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {conversations.map((convo) => (
          <button
            key={convo.id}
            onClick={() => setActiveId(convo.id)}
            className="bg-white rounded-xl shadow-sm p-6 border-2 border-gray-100 hover:border-gold-500 hover:shadow-md text-left transition-all group"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <MessageSquare className="w-5 h-5 text-gold-700" />
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${convo.level === "beginner" ? "bg-sage-light text-sage-dark" : "bg-gold-100 text-gold-700"}`}>
                    {convo.level}
                  </span>
                </div>
                <h3 className="font-bold text-gray-800 mb-1">{convo.title}</h3>
                <p className="text-sm text-gray-500">{convo.situation}</p>
                <p className="text-xs text-gray-400 mt-2">{convo.dialogue.length} lines</p>
              </div>
              <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-gold-700 transition-colors flex-shrink-0" />
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
